/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { hasAtLeast, isEmpty, isNullish } from 'remeda'
import { useEventListener } from 'usehooks-ts'
import { ConditionalShopTheLook } from '@components/ConditionalShopTheLook/ConditionalShopTheLook'
import { lazyImport } from '@helpers'
import { ZoomMinusIcon } from '@velasca/icons/zoom-minus'
import { ZoomPlusIcon } from '@velasca/icons/zoom-plus'
import type { ImageMedia, ModelMedia, VideoMedia } from '@velasca/types'
import { MagnifyButton } from '@velasca/ui/magnify-button'
import { NavigationButton } from '@velasca/ui/navigation-button'
import { ZoomedImage } from '@velasca/ui/zoomed-image'
import { ZoomedVideo } from '@velasca/ui/zoomed-video'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { handleError } from '@velasca/utils/handle-error'
import { setQueryParams } from '@velasca/utils/set-query-params'
import withLazyLoad from '../LazyLoad/LazyLoad'
import {
  ContainerGrid,
  DefectiveMedia,
  GridElement,
  Image,
  ImageSkeleton,
  LeftArrowContainer,
  Model,
  RightArrowContainer,
  VideoContainer,
  VideoTile,
  ZoomButtonStyle,
} from './ImagesPageProduct.styles'

const { SketchfabPlayer } = lazyImport(
  () => import('@velasca/ui/sketchfab-player'),
  'SketchfabPlayer',
)

const lazyConfig = {
  rootMargin: `800px 0px 800px 0px`,
  triggerOnce: true,
}

const SketchfabPlayerLazyLoad = withLazyLoad<
  React.ComponentProps<typeof SketchfabPlayer>
>((props) => {
  return (
    <Suspense fallback={null}>
      <SketchfabPlayer {...props} />
    </Suspense>
  )
}, lazyConfig)

const BIG_SIZES = [600, 750, 900, 1100, 1400, 1600]
const SMALL_SIZES = BIG_SIZES.map((e) => e / 2)

interface ImagesPageProductProps {
  medias: (ImageMedia | ModelMedia | VideoMedia)[]
  zoom?: boolean
  loading?: boolean
  scrollableImage?: boolean
  color?: string
  material?: string
  productTitle: string
  productType: string
  shopTheLookIDs?: string[]
}

export const ImagesPageProduct = ({
  medias,
  zoom = false,
  loading = false,
  scrollableImage,
  color = '',
  material = '',
  productTitle,
  productType,
  shopTheLookIDs,
}: ImagesPageProductProps) => {
  const { t } = useTranslation('product')

  const [zoomedImageIndex, setZoomedImageIndex] = useState<number | null>(null)
  const [isModelFullScreen, setIsModelFullScreen] = useState(false)
  const imageRefs = useRef<({ currentSrc: string } | null)[]>([])
  const { ref: vidRef, inView, entry } = useInView({ threshold: 0 })

  const currentMedia = useMemo(() => {
    if (zoomedImageIndex === null) {
      return null
    }

    return medias[zoomedImageIndex]
  }, [zoomedImageIndex, medias])

  const defaultModelWidth = useMemo(() => {
    const search = (
      medias?.find((media) => media?.type === 'image') as ImageMedia
    )?.width

    return search || 4
  }, [medias])

  const defaultModelHeight = useMemo(() => {
    const search = (
      medias?.find((media) => media?.type === 'image') as ImageMedia
    )?.height

    return search || 5
  }, [medias])

  const handlePrevImage = useCallback(() => {
    setZoomedImageIndex((index) => {
      // TODO: using the "non-null assertion operator" here is a trick. Currently the math operation still works even if the index is null, but this is not ideal, and should be refactored.
      return (index! + medias.length - 1) % medias.length
    })
  }, [setZoomedImageIndex, medias])

  const handleNextImage = useCallback(() => {
    setZoomedImageIndex((index) => {
      // TODO: using the "non-null assertion operator" here is a trick. Currently the math operation still works even if the index is null, but this is not ideal, and should be refactored.
      return (index! + 1) % medias.length
    })
  }, [setZoomedImageIndex, medias])

  const handleClose = useCallback(() => {
    setZoomedImageIndex(null)
    if (isModelFullScreen) {
      setIsModelFullScreen(false)
    }
  }, [setZoomedImageIndex, isModelFullScreen, setIsModelFullScreen])

  useEffect(() => {
    currentMedia?.type === 'model'
      ? setIsModelFullScreen(true)
      : setIsModelFullScreen(false)
  }, [currentMedia])

  useEffect(() => {
    if (!entry?.target) {
      return
    }

    const entryTarget = entry.target as HTMLVideoElement

    if (inView && !currentMedia) {
      entryTarget.play().catch((error) => {
        handleError(error)
      })
    } else {
      entryTarget.pause()
    }
  }, [entry, inView, currentMedia])

  const navigate = (e: KeyboardEvent) => {
    if (zoomedImageIndex === null) {
      return
    }

    switch (e.code) {
      case 'Escape': {
        handleClose()
        break
      }
      case 'ArrowRight': {
        handleNextImage()
        break
      }
      case 'ArrowLeft': {
        handlePrevImage()
        break
      }
      default: {
        return
      }
    }
  }

  useEventListener('keydown', navigate)

  if (isEmpty(medias)) {
    return null
  }

  return (
    <ContainerGrid>
      {medias?.map((media, index) => {
        const imageWidth =
          media?.type === 'image' && media?.width ? media.width : 4
        const imageHeight =
          media?.type === 'image' && media?.height ? media.height : 5

        /*check that the index is module of 3
        or once you have reached the last image
        check that the number of images is module of 3 and stretch it*/
        const isBigImage = !!(
          index % 3 === 0 ||
          (index === medias.length - 1 && medias.length % 3)
        )

        if (loading) {
          return (
            <GridElement key={index} zoom={zoom} stretch={isBigImage}>
              <ImageSkeleton
                imageWidth={imageWidth}
                imageHeight={imageHeight}
              />
            </GridElement>
          )
        }

        return (
          <GridElement key={index} zoom={zoom} stretch={isBigImage}>
            <ErrorBoundary
              fallback={<DefectiveMedia>Media unavailable</DefectiveMedia>}
            >
              <ConditionalShopTheLook
                shopTheLookIDs={shopTheLookIDs}
                condition={
                  index === 0 &&
                  Boolean(shopTheLookIDs && hasAtLeast(shopTheLookIDs, 1))
                }
              >
                {media?.type === 'model' && (
                  <Model
                    isFullScreen={isModelFullScreen}
                    width={defaultModelWidth}
                    height={defaultModelHeight}
                  >
                    {zoom && (
                      <MagnifyButton
                        onClick={() => {
                          if (isModelFullScreen) {
                            handleClose()
                          } else {
                            setZoomedImageIndex(index)
                            setIsModelFullScreen(true)
                          }
                        }}
                      >
                        {isModelFullScreen ? (
                          <ZoomMinusIcon />
                        ) : (
                          <ZoomPlusIcon />
                        )}
                      </MagnifyButton>
                    )}
                    <SketchfabPlayerLazyLoad
                      id="sketchfab-tile-player"
                      modelId={media?.id}
                      isModelFullScreen={isModelFullScreen}
                    />
                  </Model>
                )}

                {media?.type === 'video' && (
                  <VideoContainer
                    mediaWidth={media.sources[0].width}
                    mediaHeight={media.sources[0].height}
                    onClick={() => {
                      setZoomedImageIndex(index)
                    }}
                  >
                    {zoom && <ZoomButtonStyle action="open" />}

                    {media?.sources && (
                      <VideoTile
                        loop
                        autoPlay
                        muted
                        playsInline
                        controls={false}
                        controlsList="nodownload"
                        ref={vidRef}
                        onContextMenu={(e) => {
                          // needed to prevent video download on right click.
                          e.preventDefault()

                          return false
                        }}
                      >
                        {media?.sources?.map(({ src, mimeType }) => (
                          <source key={src} src={src} type={mimeType} />
                        ))}
                      </VideoTile>
                    )}
                  </VideoContainer>
                )}

                {media?.type === 'image' && (
                  <div
                    onClick={() => {
                      setZoomedImageIndex(index)
                    }}
                  >
                    {zoom && <ZoomButtonStyle action="open" />}

                    {media?.src && (
                      <Image
                        lazyload
                        lqip
                        imageWidth={imageWidth}
                        imageHeight={imageHeight}
                        transitionDuration={50}
                        imgSrc={setQueryParams(media?.src, [
                          { name: 'width', value: '30' },
                        ])}
                        sources={buildIntrinsicWidthSources({
                          src: media?.src,
                          maxWidth: media?.width,
                          sizes: isBigImage ? BIG_SIZES : SMALL_SIZES,
                        })}
                        alt={t('apparelProductPageImgAltTemplate', {
                          color,
                          material,
                          productTitle,
                          productType,
                          imgNumber: index + 1,
                        })}
                        ref={(ref) => {
                          imageRefs.current[index] = ref
                        }}
                      />
                    )}
                  </div>
                )}
              </ConditionalShopTheLook>
            </ErrorBoundary>
          </GridElement>
        )
      })}

      {currentMedia && !isNullish(zoomedImageIndex) && (
        <>
          {(currentMedia?.type === 'image' && (
            <ZoomedImage
              zoomedImg={currentMedia}
              scrollableImage={scrollableImage}
              imgSrc={imageRefs?.current[zoomedImageIndex]?.currentSrc}
              onClick={handleClose}
            />
          )) ||
            (currentMedia?.type === 'video' && (
              <ZoomedVideo media={currentMedia} handleClose={handleClose} />
            ))}
          <ZoomButtonStyle action="close" onClick={handleClose} />
        </>
      )}

      {zoom && !isNullish(zoomedImageIndex) && (
        <LeftArrowContainer>
          <NavigationButton buttonType="prev" onClick={handlePrevImage} />
        </LeftArrowContainer>
      )}
      {zoom && !isNullish(zoomedImageIndex) && (
        <RightArrowContainer>
          <NavigationButton buttonType="next" onClick={handleNextImage} />
        </RightArrowContainer>
      )}
    </ContainerGrid>
  )
}
